import { DownloadIcon } from "@chakra-ui/icons";
import { Button, VStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { connect, useSelector } from "react-redux";

import { RanaModal } from "@nens/ranalib";
import { ResultFile } from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { AppDispatch } from "../App";
import LoadingIndicatorVisual from "../components/LoadingIndicatorVisual";
import Snackbar from "../components/Snackbar";
import StartScreenBackground from "../StartScreen/StartScreenBackground";
import { backToStartScreen } from "../state/appState";
import { getSimulation, getSimulationStatus, getThreediModelNotNull } from "../state/simulation";

import heightWidthStyles from "./../styles/HeightWidth.module.css";
// we might need this later. If we want to use the download all button
// import menuStyles from './../MainScreen/Menu.module.css';
import styles from "./DownloadScreen.module.css";

interface DownloadResultsProps {
  backToStartScreen: () => void;
}

type File = ResultFile & { downloaded: boolean };

const PostProcessing: React.FC = () => {
  return (
    <RanaModal
      isOpen
      topBar={<FormattedMessage id="download.preparing" defaultMessage="Preparing files ..." />}
    >
      <LoadingIndicatorVisual />
    </RanaModal>
  );
};

const DownloadResults: React.FC<DownloadResultsProps> = (props) => {
  const threediModel = useSelector(getThreediModelNotNull);
  const simulation = useSelector(getSimulation);

  const [files, setFiles] = useState<File[] | null>(null);
  const [gridadminDownloaded, setGridadminDownloaded] = useState<boolean>(false);

  // Helper functions to download files
  const downloadFile = (fileId: number | undefined) => {
    if (!fileId) {
      console.error("Cannot find id of the result file to download");
      return;
    }
    if (simulation === null || simulation.id === undefined) return;

    const v3Api = getV3Api();
    v3Api
      .simulationsResultsFilesDownload({
        simulationPk: simulation.id,
        id: fileId,
      })
      .then((res) => {
        window.open(res.getUrl);
      })
      .catch((e) => console.error(e));
  };

  const downloadGridadminFile = () => {
    if (!threediModel.id) {
      console.error("Cannot find id of the threedi model");
      return;
    }

    const v3Api = getV3Api();
    v3Api
      .threedimodelsGridadminDownload({
        id: threediModel.id,
      })
      .then((res) => {
        window.open(res.getUrl);
      })
      .catch((e) => console.error(e));
  };

  useEffect(() => {
    // Helper function to get the result files and manually
    // add a "downloaded" parameter to the file object to
    // keep track if the file has been downloaded or not
    const receiveResultFiles = (simulationId: number) => {
      const v3Api = getV3Api();
      v3Api
        .simulationsResultsFilesList({
          simulationPk: simulationId,
        })
        .then((res) => {
          const files = res.results.map((file) => {
            return {
              ...file,
              downloaded: false,
            };
          });
          setFiles(files);
        })
        .catch((e) => console.error(e));
    };
    const simulationId = simulation?.id;
    if (simulationId === undefined) return;
    receiveResultFiles(simulationId);
  }, [simulation?.id]);

  if (!files) return null; // do nothing

  // we might need this later. If we want to use the download all button
  // const allFilesDownloaded = gridadminDownloaded && !files.map(file => file.downloaded).includes(false);

  return (
    <RanaModal
      isOpen={true}
      topBar={<FormattedMessage id="download.ready" defaultMessage="Files ready to download" />}
      onClose={props.backToStartScreen}
      buttons={[
        <Button variant="primary" onClick={props.backToStartScreen}>
          <FormattedMessage id="download.return" defaultMessage="Return to start screen" />
        </Button>,
      ]}
    >
      <VStack gap="8px">
        <div className={styles.DownloadFile}>
          <span className={styles.Filename}>gridadmin.h5</span>
          <div className={styles.Download}>
            <Button
              variant="secondary"
              leftIcon={<DownloadIcon />}
              onClick={() => {
                downloadGridadminFile();
                setGridadminDownloaded(true);
              }}
            >
              <FormattedMessage id="download.download" defaultMessage="Download" />
            </Button>
            <div
              className={styles.Checkmark}
              style={{
                visibility: gridadminDownloaded ? "visible" : "hidden",
              }}
            >
              &#10003;
            </div>
          </div>
        </div>
        {files.map((file) => (
          <div className={styles.DownloadFile} key={file.id}>
            <span className={styles.Filename}>{file.filename}</span>
            <div className={styles.Download}>
              <Button
                variant="secondary"
                leftIcon={<DownloadIcon />}
                onClick={() => {
                  downloadFile(file.id);
                  setFiles(
                    files.map((f) => {
                      if (f.id === file.id) {
                        f.downloaded = true;
                        return f;
                      }
                      return f;
                    })
                  );
                }}
              >
                <FormattedMessage id="download.download" defaultMessage="Download" />
              </Button>
              <div
                className={styles.Checkmark}
                style={{
                  visibility: file.downloaded ? "visible" : "hidden",
                }}
              >
                &#10003;
              </div>
            </div>
          </div>
        ))}
      </VStack>
    </RanaModal>
  );
};

const DownloadScreen: React.FC<DispatchProps> = (props) => {
  const status = useSelector(getSimulationStatus).status;

  return (
    <div className={heightWidthStyles.Full}>
      <StartScreenBackground>
        <Snackbar />
        {status === "finished" ? (
          <DownloadResults backToStartScreen={props.backToStartScreen} />
        ) : (
          // status === "postprocessing"
          <PostProcessing />
        )}
      </StartScreenBackground>
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  backToStartScreen: () => dispatch(backToStartScreen()),
});
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default connect(null, mapDispatchToProps)(DownloadScreen);
